import { useCallback, useReducer } from "react";
import { ValidData } from "./StoreHelper";

export type ValueOf<T> = T[keyof T] & string | number | symbol;

export type EntityValidData<T> = {
    [key in keyof T]: string | number | symbol;
} & ValidData;

export interface EntityStore<T> {
    items: T[];
    ids: string[];
    entities: EntityState<T>;
    setItem: (item: T & EntityValidData<T>) => void;
    setItems: (items: Array<T & EntityValidData<T>>) => void;
    deleteItem: (item: T & EntityValidData<T>) => void;
    deleteItems: (items: Array<T & EntityValidData<T>>) => void;
}

type EntityState<T> = {
    [id in ValueOf<T>]: T;
} | {};

interface SetItem<T> {
    type: "setItem";
    payload: T & EntityValidData<T>;
}

interface SetItems<T> {
    type: "setItems";
    payload: Array<T & EntityValidData<T>>;
}

interface DeleteItem<T> {
    type: "deleteItem";
    payload: T & EntityValidData<T>;
}

interface DeleteItems<T> {
    type: "deleteItems";
    payload: Array<T & EntityValidData<T>>;
}

export type EntityKey<T> = keyof T | 'id';

export type EntityAction<T> = SetItem<T> | SetItems<T> | DeleteItem<T> | DeleteItems<T>;

export function useEntityStore<T>(
    entityKey: EntityKey<T> = 'id'
): EntityStore<T> {

    function entityReducer(state: EntityState<T>, action: EntityAction<T>) {
        switch (action.type) {
            case "setItem":
                const key = action.payload[entityKey];
                return {...state, [key]: action.payload};
            case "setItems":
                return {
                    ...state, ...action.payload.reduce((acc, current) => {
                        return {...acc, [current[entityKey]]: current};
                    }, {})
                };
            case "deleteItem": {
                const entityState = {...state};
                // @ts-ignore
                delete entityState[action.payload];
                return entityState;
            }
            case "deleteItems": {
                const entityState = {...state};
                // @ts-ignore
                action.payload.forEach(payload => delete entityState[payload]);
                return entityState;
            }
            default:
                return state;
        }
    }

    const [state, dispatcher] = useReducer(entityReducer, {});

    return {
        items: Object.values(state),
        ids: Object.keys(state),
        entities: {...state},
        setItem: useCallback((payload) => {
            dispatcher({type: "setItem", payload})
        }, []),
        setItems: useCallback((payload) => {
            dispatcher({type: "setItems", payload})
        }, []),
        deleteItem: useCallback((payload) => {
            dispatcher({type: "deleteItem", payload})
        }, []),
        deleteItems: useCallback((payload) => {
            dispatcher({type: "deleteItems", payload})
        }, []),
    }
}
